import React, { useState } from 'react';

import {
  applyResponsiveTypography,
  Chip,
  Checkbox,
  ToolTip,
  BaseButton,
  FilterBottomSheet,
  useIsMobile,
  useT,
} from '@repo/common';
import SortByList from './SortByList';

const responsiveTypo = (mobileKey, desktopKey, value) =>
  applyResponsiveTypography(mobileKey, desktopKey, value);

const SORT_OPTIONS = ['By Date', 'By Deadline'];

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/20px/icon_20px_delete_outline.svg';
import { ReactComponent as InvisibleIcon } from '@repo/common/assets/icons/20px/icon_20px_invisible-close_outline.svg';
import { ReactComponent as VisibleIcon } from '@repo/common/assets/icons/20px/icon_20px_visible_outline.svg';
import { ReactComponent as FolderIcon } from '@repo/common/assets/icons/20px/icon_20px_folder_outline.svg';

const HEADER_ACTION_BY_STATUS = {
  all: ['delete', 'archive'],
  active: ['delete', 'hide', 'archive'],
  hidden: ['delete', 'unhide', 'archive'],
  drafts: ['delete', 'archive'],
  closed: ['delete', 'unhide', 'archive'],
  trash: ['deletePermanent'],
};

const JobListHeader = ({
  status,
  isAdmin,
  isDetail,
  forceShowActions = false,
  totalCount,
  checked,
  onToggleAll,
  selectedCount = 0,
  onDeleteSelected,
  onHideSelected,
  onUnhideSelected,
  onDeletePermanentSelected,
  sortBy = 'By Date',
  onSortChange,
  isFilterApplied = false,
  hideArchive = false,
  onManageJobs,
  // 개수 문구 대신 보여줄 제목 (모바일 공고 관리: 현재 상태명)
  title,
}) => {
  const t = useT();
  const isMobile = useIsMobile();
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortAnchorRef = React.useRef(null);
  const hasSelection = selectedCount > 0;
  const shouldShowActions = forceShowActions || hasSelection;
  const actions = HEADER_ACTION_BY_STATUS[status] || [];

  /* FilterBottomSheet는 라벨 문자열을 그대로 주고받으므로 정방향/역방향 매핑을 둔다 */
  const sortLabelOf = (value) =>
    value === 'By Deadline'
      ? t('By Deadline', '마감일순')
      : t('By Date', '날짜순');
  const sortValueOf = (label) =>
    SORT_OPTIONS.find((option) => sortLabelOf(option) === label) ?? 'By Date';
  const sortLabel = sortLabelOf(sortBy);

  return (
    <div className='relative flex items-center justify-between w-full shrink-0'>
      <div className='flex flex-[1_0_0] flex-row items-center self-stretch'>
        {/* 모바일 공고 관리는 일괄 선택 없이 카드별 옵션만 쓴다 */}
        {!isDetail && isAdmin && !isMobile && (
          <div className='pr-2'>
            <Checkbox
              checked={checked}
              onChange={(e) => onToggleAll(e.target.checked)}
              boxClassName='border-icon-tertiary'
            />
          </div>
        )}
        {shouldShowActions && (
          <div className='flex items-center'>
            <div className='flex items-center pr-2'>
              {actions.includes('delete') && (
                <div className='relative px-2 group'>
                  <ToolTip
                    label={t('Move to Trash', '휴지통으로 이동')}
                    position='bottom'
                    align='center'
                    state='accent'
                    className='absolute bottom-full -translate-y-1 -translate-x-1/2 left-[17.5px] mb-2 opacity-0 group-hover:opacity-100 transition-opacity'
                  />
                  <DeleteIcon
                    className='cursor-pointer text-icon-tertiary active:text-icon-placeholder'
                    onClick={onDeleteSelected}
                  />
                </div>
              )}

              {actions.includes('hide') && (
                <div className='relative px-2 group'>
                  <ToolTip
                    label={t('Hide', '숨기기')}
                    position='bottom'
                    align='center'
                    state='accent'
                    className='absolute bottom-full -translate-y-1 -translate-x-1/2 left-[17.5px] mb-2 opacity-0 group-hover:opacity-100 transition-opacity'
                  />
                  <InvisibleIcon
                    className='cursor-pointer text-icon-tertiary active:text-icon-placeholder'
                    onClick={onHideSelected}
                  />
                </div>
              )}

              {actions.includes('unhide') && (
                <div className='relative px-2 group'>
                  <ToolTip
                    label={t('Set as Active', '활성 상태로 설정')}
                    position='bottom'
                    align='center'
                    state='accent'
                    className='absolute bottom-full -translate-y-1 -translate-x-1/2 left-[17.5px] mb-2 opacity-0 group-hover:opacity-100 transition-opacity'
                  />
                  <VisibleIcon
                    className='cursor-pointer text-icon-tertiary active:text-icon-placeholder'
                    onClick={onUnhideSelected}
                  />
                </div>
              )}

              {actions.includes('archive') && !hideArchive && (
                <div className='relative px-2 group'>
                  <FolderIcon className='text-icon-disable' />
                </div>
              )}

              {actions.includes('deletePermanent') && (
                <div className='relative px-2 group'>
                  <BaseButton
                    hideIcon
                    bgclass='bg-grayscale-gray00 border-none'
                    textclass='text-text-tertiary'
                    hoverclass='hover:bg-grayscale-gray01 hover:text-text-tertiary'
                    activeclass='active:bg-grayscale-gray01 active:text-text-tertiary'
                    onClick={onDeletePermanentSelected}
                  >
                    {t('Delete permanently', '영구 삭제')}
                  </BaseButton>
                </div>
              )}
            </div>
            {/* 모바일 상세 상단 바는 구분선 없이 아이콘만 나열한다 */}
            {!(isDetail && isMobile) && (
              <div className='w-px h-6 bg-grayscale-gray02' />
            )}
          </div>
        )}
        {!isDetail && (
          <div
            className={`flex flex-[1_0_0] h-full items-center justify-center min-h-px min-w-px px-0 tabletV:px-6 py-0 relative ${
              isAdmin ? 'tabletV:pl-4' : ''
            }`}
          >
            <p
              className={`${onManageJobs ? '' : 'flex-[1_0_0]'} ${responsiveTypo(
                isFilterApplied ? 'b14Strong' : 'b13',
                isFilterApplied ? (isAdmin ? 'b14Strong' : 'b18Strong') : 'b16',
              )} min-h-px min-w-px not-italic relative whitespace-pre-wrap ${
                isFilterApplied
                  ? 'pt-1 text-text-accent'
                  : 'text-text-secondary'
              }`}
            >
              {title && !isFilterApplied
                ? title
                : isFilterApplied
                ? t(
                    `Showing: ${totalCount ?? 0} filtered jobs`,
                    `필터링된 채용 공고 ${totalCount ?? 0}개 표시`,
                  )
                : isMobile
                  ? t(
                      `Showing ${totalCount ?? 0} jobs`,
                      `채용 공고 ${totalCount ?? 0}개`,
                    )
                  : t(
                      `${totalCount ?? 0} results total`,
                      `전체 ${totalCount ?? 0}개 결과`,
                    )}
            </p>
            {onManageJobs && (
              <button type='button' onClick={onManageJobs} className='ml-1.5 tabletV:ml-4 mr-auto shrink-0 px-0.5 py-[5px] tabletV:p-0 typo-b12 text-text-secondary underline underline-offset-2'>
                {t('Manage Jobs', '공고 관리')}
              </button>
            )}
          </div>
        )}
      </div>

      {!isDetail && (
        <div ref={sortAnchorRef} className='relative'>
          <Chip
            icon={isSortOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
            iconPosition='after'
            disableTypography={true}
            bgclass='bg-transparent typo-b13Strong tabletV:typo-b14 h-[26px] tabletV:h-[34px] gap-1 tabletV:gap-2 rounded-lg tabletV:rounded-full pl-1 pr-2 py-1 tabletV:px-4 tabletV:py-3'
            borderclass='ring-0'
            textclass='text-grayscale-gray07'
            iconclass='text-icon-primary size-3 tabletV:size-4'
            onClick={(e) => {
              e.stopPropagation();
              setIsSortOpen((prev) => !prev);
            }}
          >
            {sortLabel}
          </Chip>

          {/* 데스크탑: 앵커 드롭다운 */}
          {!isMobile && isSortOpen && (
            <div className='absolute top-[42px] right-0 z-10'>
              <SortByList
                selected={sortBy}
                onSelect={(value) => {
                  onSortChange?.(value);
                  setIsSortOpen(false);
                }}
              />
            </div>
          )}

          {/* 모바일: 공용 FilterBottomSheet */}
          {isMobile && isSortOpen && (
            <FilterBottomSheet
              options={SORT_OPTIONS.map(sortLabelOf)}
              selected={sortLabel}
              onSelect={(label) => {
                onSortChange?.(sortValueOf(label));
                setIsSortOpen(false);
              }}
              onClose={() => setIsSortOpen(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default JobListHeader;
